'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';
import {
  TrendingDown,
  Target,
  Megaphone,
  LineChart,
  Users,
  Layers,
  ShieldAlert,
  Cog,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

const challenges = [
  {
    id: 'ch-growth-strategy',
    icon: TrendingDown,
    number: '01',
    title: 'Unclear growth strategy',
    description:
      "You know you need to grow but don't have a clear roadmap for where to focus next.",
    service: 'Business Consulting',
    serviceId: 'svc-consulting',
    serviceHref: '#services',
  },
  {
    id: 'ch-positioning',
    icon: Target,
    number: '02',
    title: 'Weak market positioning',
    description: "Customers don't clearly understand why they should choose you over competitors.",
    service: 'Digital Design & Branding',
    serviceId: 'svc-design',
    serviceHref: '#services',
  },
  {
    id: 'ch-leads',
    icon: Megaphone,
    number: '03',
    title: 'Inconsistent lead generation',
    description:
      'New customers come in bursts but you have no reliable, repeatable acquisition system.',
    service: 'Paid Ads Management',
    serviceId: 'svc-ads',
    serviceHref: '#services',
  },
  {
    id: 'ch-conversion',
    icon: LineChart,
    number: '04',
    title: 'Low conversion rates',
    description: 'Traffic and inquiries come in but not enough turn into paying customers.',
    service: 'SEO & Content Creation',
    serviceId: 'svc-seo',
    serviceHref: '#services',
  },
  {
    id: 'ch-marketing',
    icon: Layers,
    number: '05',
    title: 'Ineffective marketing spend',
    description: "You're spending on ads and content but can't see what's actually working.",
    service: 'Social Media & Digital Marketing',
    serviceId: 'svc-social',
    serviceHref: '#services',
  },
  {
    id: 'ch-segments',
    icon: Users,
    number: '06',
    title: 'Unclear customer segments',
    description: "You're trying to serve everyone and ending up resonating with no one.",
    service: 'Business Consulting',
    serviceId: 'svc-consulting',
    serviceHref: '#services',
  },
  {
    id: 'ch-brand',
    icon: ShieldAlert,
    number: '07',
    title: 'Brand differentiation problems',
    description: 'Your brand looks and sounds like every other business in your category.',
    service: 'Digital Design & Branding',
    serviceId: 'svc-design',
    serviceHref: '#services',
  },
  {
    id: 'ch-scaling',
    icon: Cog,
    number: '08',
    title: 'Operational scaling challenges',
    description:
      'Growth is happening but your processes and systems are breaking under the pressure.',
    service: 'Business Consulting',
    serviceId: 'svc-consulting',
    serviceHref: '#services',
  },
];

function RecommendationPanel({
  challenge,
  onViewService,
}: {
  challenge: (typeof challenges)[0];
  onViewService: () => void;
}) {
  return (
    <motion.div
      key={challenge.id}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 p-6"
        style={{
          background: 'var(--m-bg)',
          borderLeft: '3px solid var(--m-ink)',
          border: '1px solid var(--m-rule)',
          borderLeftWidth: 3,
          borderLeftColor: 'var(--m-ink)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              border: '1px solid rgba(var(--m-ink-rgb),0.1)',
              background: 'rgba(var(--m-ink-rgb),0.03)',
            }}
          >
            <challenge.icon className="w-5 h-5" style={{ color: 'var(--m-ink-3)' }} />
          </div>
          <div>
            <p
              className="font-mono text-[11px] uppercase tracking-widest mb-1"
              style={{ color: 'var(--m-muted-3)', letterSpacing: '0.18em' }}
            >
              Recommended service
            </p>
            <p
              className="font-bold uppercase mb-1"
              style={{
                fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                fontSize: 16,
                color: 'var(--m-ink)',
                letterSpacing: '0.03em',
              }}
            >
              {challenge.service}
            </p>
            <p className="text-sm" style={{ color: 'var(--m-ink-6)', lineHeight: 1.55 }}>
              {challenge.description}
            </p>
          </div>
        </div>
        {/* Full-width, stacked and thumb-sized on a phone; unchanged from
            sm upwards. */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto sm:flex-shrink-0">
          <button
            onClick={onViewService}
            className="font-mono uppercase tracking-widest transition-all flex items-center justify-center sm:justify-start gap-2 min-h-[44px] sm:min-h-0"
            style={{
              border: '1px solid rgba(var(--m-ink-rgb),0.15)',
              color: 'var(--m-ink-5)',
              fontSize: 11,
              letterSpacing: '0.12em',
              padding: '10px 16px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(var(--m-ink-rgb),0.35)';
              (e.currentTarget as HTMLElement).style.color = 'var(--m-ink)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(var(--m-ink-rgb),0.15)';
              (e.currentTarget as HTMLElement).style.color = 'var(--m-ink-5)';
            }}
          >
            View Service <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <Link
            href="/business-assessment"
            className="font-mono uppercase tracking-widest transition-all flex items-center justify-center sm:justify-start gap-2 min-h-[44px] sm:min-h-0"
            style={{
              background: 'var(--m-ink)',
              color: 'var(--m-on-ink)',
              fontSize: 11,
              letterSpacing: '0.12em',
              padding: '10px 16px',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--m-ink-3)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--m-ink)';
            }}
          >
            Full Assessment <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function ChallengesSection() {
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const selectedChallenge = challenges.find((c) => c.id === selected);

  /* The mapping from challenge to service lives in the data above and stays
     there; this only announces the answer. The services section listens, and
     because its six cards are now a horizontal carousel it is the only thing
     that can place one — it moves the row to the recommended card and scrolls
     the page to it, then marks the event handled.

     The scroll below is what used to happen here and is kept for the case where
     nothing answered: a challenge grid rendered without the services section
     should still take a visitor to the services page section. Listeners run
     synchronously, so `handled` is already accurate by the time it is read. */
  const handleViewService = () => {
    if (!selectedChallenge) return;
    const detail: { serviceId: string; handled: boolean } = {
      serviceId: selectedChallenge.serviceId,
      handled: false,
    };
    window.dispatchEvent(new CustomEvent('highlight-service', { detail }));
    if (detail.handled) return;
    setTimeout(() => {
      const cardEl = document.getElementById(`service-card-${selectedChallenge.serviceId}`);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const el = document.getElementById('services');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  };

  const selectedIndex = selected ? challenges.findIndex((c) => c.id === selected) : -1;

  // The panel spans a whole row, so it can only be inserted at a row boundary
  // without leaving a hole. That boundary depends on the column count, which is
  // 4 from lg up and 2 below it — a fact only the browser knows.
  const [columns, setColumns] = useState(2);
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 1024px)');
    const apply = () => setColumns(wide.matches ? 4 : 2);
    apply();
    wide.addEventListener('change', apply);
    return () => wide.removeEventListener('change', apply);
  }, []);

  const items: React.ReactNode[] = [];
  challenges.forEach((challenge, i) => {
    const isSelected = selected === challenge.id;
    items.push(
      <motion.button
        key={challenge.id}
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: i * 0.06, duration: 0.5 }}
        onClick={() => setSelected(isSelected ? null : challenge.id)}
        className="text-left group relative overflow-hidden flex flex-col h-full transition-all duration-200"
        style={{
          background: isSelected ? 'var(--m-bg)' : 'rgba(var(--m-on-ink-rgb),0.5)',
          boxShadow: isSelected ? 'var(--m-lift-2), var(--m-lift-edge)' : 'var(--m-lift-1)',
          // 0.07 was close enough to invisible that the eight cards read as one
          // undivided block of text rather than eight things you can press.
          borderTop: '1px solid rgba(var(--m-ink-rgb),0.14)',
          borderRight: '1px solid rgba(var(--m-ink-rgb),0.14)',
          borderBottom: '1px solid rgba(var(--m-ink-rgb),0.14)',
          borderLeft: isSelected ? '3px solid var(--m-ink)' : '3px solid transparent',
          padding: '20px 20px 20px 18px',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            const card = e.currentTarget as HTMLElement;
            card.style.background = 'var(--m-bg)';
            card.style.borderLeftColor = 'rgba(var(--m-ink-rgb),0.35)';
            // A shadow on hover is the plainest signal that something is
            // pressable. Deliberately not a translate: framer-motion owns this
            // element's transform for the scroll-in animation, and writing to
            // it here would fight that and can leave the card stuck off-axis.
            card.style.borderTopColor = 'rgba(var(--m-ink-rgb),0.35)';
            card.style.borderRightColor = 'rgba(var(--m-ink-rgb),0.35)';
            card.style.borderBottomColor = 'rgba(var(--m-ink-rgb),0.35)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            const card = e.currentTarget as HTMLElement;
            card.style.background = 'rgba(var(--m-on-ink-rgb),0.5)';
            card.style.borderLeftColor = 'transparent';
            card.style.borderTopColor = 'rgba(var(--m-ink-rgb),0.14)';
            card.style.borderRightColor = 'rgba(var(--m-ink-rgb),0.14)';
            card.style.borderBottomColor = 'rgba(var(--m-ink-rgb),0.14)';
          }
        }}
      >
        {/* Number label */}
        <div className="flex items-start justify-between mb-3">
          <span
            className="font-mono"
            style={{
              fontSize: 11,
              color: isSelected ? 'var(--m-muted)' : 'var(--m-muted-4)',
              letterSpacing: '0.14em',
            }}
          >
            {challenge.number}
          </span>
          <div
            className="flex items-center justify-center flex-shrink-0 transition-all duration-200"
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${isSelected ? 'rgba(var(--m-ink-rgb),0.15)' : 'rgba(var(--m-ink-rgb),0.07)'}`,
              background: isSelected ? 'rgba(var(--m-ink-rgb),0.04)' : 'transparent',
            }}
          >
            <challenge.icon
              className="w-4 h-4 transition-colors duration-200"
              style={{
                color: isSelected ? 'var(--m-ink-3)' : 'var(--m-muted-3)',
                width: 14,
                height: 14,
              }}
            />
          </div>
        </div>

        <h3
          className="font-bold uppercase mb-2 leading-tight"
          style={{
            fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
            /* Was 13px, sitting above its own 13px body copy — a heading no
               larger than the text it labels, which is why eight cards of
               condensed caps read as a wall rather than as eight choices. */
            fontSize: 17,
            fontWeight: 600,
            color: isSelected ? 'var(--m-ink)' : 'var(--m-ink)',
            letterSpacing: '0.04em',
            lineHeight: 1.25,
          }}
        >
          {challenge.title}
        </h3>
        <p
          className={`leading-relaxed ${isSelected ? '' : 'hidden sm:block'}`}
          style={{
            // Was --m-muted at 11px: 3.25:1 against this section's background,
            // below the 4.5:1 the standard asks for, and the smallest body text
            // on the site. --m-ink-6 measures 5.26:1 here.
            color: isSelected ? 'var(--m-ink-5)' : 'var(--m-ink-6)',
            lineHeight: 1.6,
            fontSize: 15,
          }}
        >
          {challenge.description}
        </p>

        {/* The card is a button, but its 7%-black border and bare text read as
            a list item. This says out loud that pressing it does something —
            and it is always present, so the affordance is visible before the
            first press rather than only after it. */}
        <div className="flex items-center gap-1.5 mt-auto pt-4">
          <div
            className="transition-all duration-200"
            style={{
              width: isSelected ? 12 : 8,
              height: 1,
              background: isSelected ? 'var(--m-ink)' : 'var(--m-muted-3)',
            }}
          />
          <span
            className="font-mono uppercase"
            style={{
              fontSize: 11,
              letterSpacing: '0.14em',
              color: isSelected ? 'var(--m-ink-5)' : 'var(--m-ink-6)',
            }}
          >
            {isSelected ? 'See recommendation' : 'See the fix'}
          </span>
          <ArrowRight
            className="transition-transform duration-200"
            style={{
              width: 11,
              height: 11,
              color: isSelected ? 'var(--m-ink-5)' : 'var(--m-ink-6)',
              transform: isSelected ? 'translateX(2px)' : 'none',
            }}
            aria-hidden="true"
          />
        </div>
      </motion.button>
    );
  });

  // The panel goes after the last tile of the row the selected tile sits in, so
  // it appears directly beneath that tile's row instead of at the bottom of the
  // whole grid, and lands on a row boundary so no column is left empty.
  const gridItems = [...items];
  if (selectedChallenge) {
    const index = challenges.findIndex((c) => c.id === selectedChallenge.id);
    const endOfRow = Math.min((Math.floor(index / columns) + 1) * columns, items.length);
    gridItems.splice(
      endOfRow,
      0,
      <AnimatePresence key={`panel-${selectedChallenge.id}`} mode="wait">
        <div className="col-span-2 lg:col-span-4">
          <RecommendationPanel challenge={selectedChallenge} onViewService={handleViewService} />
        </div>
      </AnimatePresence>
    );
  }

  return (
    <section
      data-ground
      id="challenges"
      className="section-rhythm"
      ref={ref}
      style={{
        background:
          'linear-gradient(180deg, transparent 0%, var(--m-panel) 7%, var(--m-panel) 93%, transparent 100%)',
      }}
    >
      <div className="m-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div style={{ width: 24, height: 1, background: 'rgba(var(--m-ink-rgb),0.3)' }} />
                <span
                  className="font-mono text-[11px] uppercase tracking-widest"
                  style={{ color: 'var(--m-muted)', letterSpacing: '0.22em' }}
                >
                  Diagnostic — Select your challenge
                </span>
              </div>
              <h2 className={SECTION_HEADING_CLASS} style={SECTION_HEADING_STYLE}>
                What&apos;s holding
                <br />
                your business back?
              </h2>
            </div>
            <p
              className="max-w-xs text-sm leading-relaxed"
              style={{ color: 'var(--m-ink-6)', lineHeight: 1.7 }}
            >
              Select the challenge that resonates most. We&apos;ll show you the service best
              positioned to solve it.
            </p>
          </div>
        </motion.div>

        {/* Challenge grid */}
        {/* Two-up on phones. Eight full-width cards read as a list to be worked
            through; eight compact tiles read as a choice to be made, which is what
            this section actually asks for. The detail line stays for the selected
            card and moves to the result below for the rest. */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">{gridItems}</div>
      </div>
    </section>
  );
}
